import clsx from 'clsx';

import { DiscoverySteps, type DiscoveryStep } from '../api';

interface DiscoveryProgressBarProps {
  percentComplete: number;
  currentStep: string;
  lastMessage?: string | null | undefined;
}

// Spanish labels keyed by DiscoveryStep enum names. Unknown values fall through
// to the raw string so a forward-compatible API change still renders something.
const STEP_LABELS: Record<DiscoveryStep, string> = {
  Queued: 'En cola',
  ExtractingOcr: 'Extrayendo OCR…',
  AnalyzingFields: 'Analizando campos…',
  CalculatingMaturity: 'Calculando madurez…',
  GeneratingRecommendations: 'Generando recomendaciones…',
  PersistingProposal: 'Guardando propuesta…',
  Completed: 'Completado',
  Failed: 'Fallido',
};

function labelFor(step: string): string {
  if ((Object.values(DiscoverySteps) as string[]).includes(step)) {
    return STEP_LABELS[step as DiscoveryStep];
  }
  return step;
}

/**
 * Horizontal progress bar driven by the backend's `DiscoveryProgress` payload.
 * Animates width transitions and renders a localised step label plus an
 * optional secondary message.
 */
export function DiscoveryProgressBar({
  percentComplete,
  currentStep,
  lastMessage,
}: DiscoveryProgressBarProps): JSX.Element {
  const safePercent = Math.max(0, Math.min(100, Math.round(percentComplete)));
  const isFailed = currentStep === DiscoverySteps.Failed;
  const label = labelFor(currentStep);

  return (
    <div className="w-full space-y-2" data-testid="discovery-progress-bar">
      <div className="flex items-center justify-between text-sm">
        <span
          className={clsx(
            'font-semibold',
            isFailed ? 'text-rose-700' : 'text-slate-800',
          )}
          data-testid="discovery-progress-step"
        >
          {label}
        </span>
        <span
          className="tabular-nums text-slate-500"
          data-testid="discovery-progress-percent"
        >
          {safePercent}%
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className={clsx(
            'h-full rounded-full transition-[width] duration-500 ease-out',
            isFailed ? 'bg-rose-500' : 'bg-emerald-500',
          )}
          style={{ width: `${safePercent}%` }}
          role="progressbar"
          aria-valuenow={safePercent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {lastMessage !== undefined && lastMessage !== null && lastMessage.length > 0 ? (
        <p className="text-xs text-slate-500" data-testid="discovery-progress-message">
          {lastMessage}
        </p>
      ) : null}
    </div>
  );
}
